import React, { useRef, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { APIUrl } from '../../common/apiConfig';
import { parseCsv, toCsv, downloadCsv } from './parseCsv';

/**
 * Bulk roster import.
 *
 * "The most support-heavy screen in the product; budget for error reporting"
 * -- 11-phased-plan.md. Everything below follows from taking that seriously.
 *
 * FOUR STEPS, AND THE THIRD IS THE POINT
 * --------------------------------------
 *   1. choose students or teachers
 *   2. pick the file -- parsed in the browser, nothing sent yet
 *   3. CHECK -- the whole file is validated and every problem is listed at once
 *   4. IMPORT -- the good rows are written, in chunks, with real progress
 *
 * Step 3 exists because an 800-row export from whatever system the school
 * already runs will have bad rows in it, and finding that out one row at a time
 * while half the file is already loaded is the worst possible way to learn it.
 * Nothing is written until the administrator has seen the whole list and
 * pressed Import.
 *
 * Errors are downloadable as a CSV of their own, because the fix happens in
 * Excel and reading row numbers off a screen while editing a spreadsheet is how
 * the wrong row gets edited.
 *
 * WHY THE PROGRESS BAR IS REAL AND NOT DECORATIVE
 * -----------------------------------------------
 * Hashing one password costs about a fifth of a second, so 800 students is
 * nearly three minutes of work. That cannot be one request -- it would pass the
 * server's timeout, and a tab that shows nothing for three minutes is
 * indistinguishable from one that has crashed. So the rows go up 25 at a time
 * and the bar moves after each batch. Interrupting is safe: every write is an
 * upsert, so re-importing the same file finishes the job rather than doubling
 * it.
 *
 * THE PASSWORD LIST IS DOWNLOADED, NOT DISPLAYED
 * ----------------------------------------------
 * Every created account gets a generated password, returned once and stored
 * nowhere. For 800 students that is a list to print and cut up, not something
 * to read off a screen -- so it is offered as a CSV, and the warning says
 * plainly that closing the page loses it. The only remedy afterwards is
 * resetting each account one at a time.
 */

type Kind = 'students' | 'teachers';
type RowErr = { row: number; login: string; problem: string };

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`,
});

const TEMPLATES: Record<Kind, string[]> = {
    students: [
        'login', 'full_name', 'class', 'section', 'roll_number',
        'admission_no', 'guardian_name', 'guardian_phone',
    ],
    teachers: ['login', 'full_name', 'employee_no', 'phone', 'class', 'section', 'subject'],
};

export const RosterImport = () => {
    const notify = useNotify();
    const fileRef = useRef<HTMLInputElement>(null);

    const [kind, setKind] = useState<Kind>('students');
    const [filename, setFilename] = useState<string>('');
    const [parsed, setParsed] = useState<Record<string, string>[] | null>(null);
    const [headers, setHeaders] = useState<string[]>([]);

    const [checking, setChecking] = useState(false);
    const [checked, setChecked] = useState<any>(null);

    const [importing, setImporting] = useState(false);
    const [done, setDone] = useState(0);
    const [outcome, setOutcome] = useState<any>(null);

    const reset = () => {
        setParsed(null);
        setHeaders([]);
        setChecked(null);
        setOutcome(null);
        setDone(0);
        setFilename('');
        if (fileRef.current) fileRef.current.value = '';
    };

    // ------------------------------------------------------------------ 2 --
    const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setChecked(null);
        setOutcome(null);
        setFilename(file.name);

        const text = await file.text();
        const result = parseCsv(text);

        if (result.problem) {
            notify(result.problem, { type: 'warning' });
            setParsed(null);
            setHeaders([]);
            return;
        }
        setHeaders(result.headers);
        setParsed(result.rows);
    };

    // ------------------------------------------------------------------ 3 --
    const check = async () => {
        if (!parsed) return;
        setChecking(true);
        setOutcome(null);
        try {
            const res = await fetch(`${APIUrl}roster-import/validate`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ kind, filename, rows: parsed }),
            });
            const json = await res.json();
            if (json.status !== 'success') {
                notify(json.message || 'That file could not be checked.', { type: 'error' });
                return;
            }
            setChecked(json.result);
        } catch (err: any) {
            notify(err.message || 'That file could not be checked.', { type: 'error' });
        } finally {
            setChecking(false);
        }
    };

    // ------------------------------------------------------------------ 4 --
    const runImport = async () => {
        if (!checked || checked.valid === 0) return;

        setImporting(true);
        setDone(0);

        const rows: any[] = checked.rows;
        const size: number = checked.chunk_size || 25;
        const passwords: [string, string, string][] = [];
        let created = 0;
        let updated = 0;
        let failed = 0;
        const failures: RowErr[] = [];

        try {
            for (let i = 0; i < rows.length; i += size) {
                const chunk = rows.slice(i, i + size);

                const res = await fetch(`${APIUrl}roster-import/commit`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({ import_id: checked.import_id, kind, rows: chunk }),
                });
                const json = await res.json();

                if (json.status !== 'success') {
                    notify(
                        `${json.message || 'A batch failed.'} ${created + updated} of ${
                            rows.length
                        } rows were imported. Importing the same file again will carry on from here.`,
                        { type: 'error' }
                    );
                    break;
                }

                created += json.result.created;
                updated += json.result.updated;
                failed += json.result.failed;

                json.result.results.forEach((r: any) => {
                    if (r.password) passwords.push([r.login, r.password, String(r.row)]);
                    if (r.outcome === 'failed') {
                        failures.push({ row: r.row, login: r.login, problem: r.problem });
                    }
                });

                setDone(Math.min(i + size, rows.length));
            }

            await fetch(`${APIUrl}roster-import/finish`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ import_id: checked.import_id }),
            });

            setOutcome({ created, updated, failed, passwords, failures });
        } catch (err: any) {
            notify(err.message || 'The import stopped.', { type: 'error' });
        } finally {
            setImporting(false);
        }
    };

    const downloadErrors = () => {
        if (!checked) return;
        downloadCsv(
            `import-problems-${kind}.csv`,
            toCsv(['row', 'login', 'problem'], checked.errors.map((e: RowErr) => [e.row, e.login, e.problem]))
        );
    };

    const downloadPasswords = () => {
        if (!outcome) return;
        downloadCsv(
            `passwords-${kind}-${new Date().toISOString().slice(0, 10)}.csv`,
            toCsv(['login', 'password', 'row'], outcome.passwords)
        );
    };

    const downloadTemplate = () => {
        downloadCsv(`${kind}-template.csv`, toCsv(TEMPLATES[kind], []));
    };

    // =====================================================================

    return (
        <Card sx={{ mt: 2 }}>
            <Title title="Import a roster" />
            <CardContent>
                {/* ---------------------------------------------------- 1 -- */}
                <Typography variant="h6" gutterBottom>
                    1. What are you importing?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                    <Select
                        size="small"
                        value={kind}
                        onChange={e => {
                            setKind(e.target.value as Kind);
                            reset();
                        }}
                        sx={{ minWidth: 180 }}
                    >
                        <MenuItem value="students">Students</MenuItem>
                        <MenuItem value="teachers">Teachers</MenuItem>
                    </Select>
                    <Button size="small" onClick={downloadTemplate}>
                        Download a blank template
                    </Button>
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Column names do not have to match exactly &mdash; <b>Roll No.</b>,{' '}
                    <b>roll_number</b> and <b>RollNo</b> are all understood. Extra columns are
                    ignored.
                    {kind === 'students' ? (
                        <>
                            {' '}
                            A student needs a <b>login</b>, a <b>name</b>, and a <b>class</b> and{' '}
                            <b>section</b> that already exist.
                        </>
                    ) : (
                        <>
                            {' '}
                            A teacher needs a <b>login</b> and a <b>name</b>. Add a <b>class</b> and{' '}
                            <b>section</b> to assign them at the same time &mdash; a teacher with no
                            class can see nothing.
                        </>
                    )}
                </Alert>

                {/* ---------------------------------------------------- 2 -- */}
                <Typography variant="h6" gutterBottom>
                    2. Choose the file
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                    <Button variant="outlined" component="label">
                        Choose a CSV file
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv,text/csv"
                            hidden
                            onChange={onFile}
                        />
                    </Button>
                    {filename && <Chip label={filename} onDelete={reset} />}
                </Box>

                {parsed && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {parsed.length} row{parsed.length === 1 ? '' : 's'} read. Columns found:{' '}
                            {headers.join(', ')}
                        </Typography>

                        {/* The first three rows, so a mis-parsed file is
                            obvious BEFORE anything is sent anywhere. */}
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {headers.map(h => (
                                            <TableCell key={h}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {parsed.slice(0, 3).map((r, i) => (
                                        <TableRow key={i}>
                                            {headers.map(h => (
                                                <TableCell key={h}>{r[h]}</TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Button variant="contained" onClick={check} disabled={checking}>
                            {checking ? 'Checking…' : '3. Check the file'}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                            Nothing is saved by checking.
                        </Typography>
                    </>
                )}

                {/* ---------------------------------------------------- 3 -- */}
                {checked && !outcome && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            The file was checked
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Chip color="success" label={`${checked.valid} ready to import`} />
                            {checked.failed > 0 && (
                                <Chip color="error" label={`${checked.failed} with problems`} />
                            )}
                        </Box>

                        {checked.failed > 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <AlertTitle>
                                    {checked.failed} row{checked.failed === 1 ? '' : 's'} cannot be
                                    imported
                                </AlertTitle>
                                Importing now will load the {checked.valid} good row
                                {checked.valid === 1 ? '' : 's'} and skip these. You can also fix
                                them in the file and check it again &mdash; re-importing the whole
                                file is safe, because rows that already loaded are simply updated.
                                <Box sx={{ mt: 1 }}>
                                    <Button size="small" variant="outlined" onClick={downloadErrors}>
                                        Download the problem list
                                    </Button>
                                </Box>
                            </Alert>
                        )}

                        {checked.errors.length > 0 && (
                            <TableContainer
                                component={Paper}
                                variant="outlined"
                                sx={{ mb: 2, maxHeight: 320 }}
                            >
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Row</TableCell>
                                            <TableCell>Login</TableCell>
                                            <TableCell>Problem</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {checked.errors.map((e: RowErr, i: number) => (
                                            <TableRow key={i}>
                                                <TableCell>{e.row}</TableCell>
                                                <TableCell>{e.login}</TableCell>
                                                <TableCell>{e.problem}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {checked.truncated && (
                            <Typography variant="caption" color="text.secondary">
                                Only the first {checked.errors.length} problems are shown.
                            </Typography>
                        )}

                        {checked.valid > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={runImport}
                                    disabled={importing}
                                >
                                    {importing
                                        ? `Importing… ${done} of ${checked.valid}`
                                        : `4. Import ${checked.valid} row${
                                              checked.valid === 1 ? '' : 's'
                                          }`}
                                </Button>

                                {importing && (
                                    <Box sx={{ mt: 2 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={(done / checked.valid) * 100}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            Do not close this page. Each account needs its password
                                            hashed, which is deliberately slow.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {/* ---------------------------------------------------- 5 -- */}
                {outcome && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Import finished
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Chip color="success" label={`${outcome.created} added`} />
                            <Chip label={`${outcome.updated} updated`} />
                            {outcome.failed > 0 && (
                                <Chip color="error" label={`${outcome.failed} failed`} />
                            )}
                        </Box>

                        {outcome.passwords.length > 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <AlertTitle>
                                    Download the password list now &mdash; this is the only chance
                                </AlertTitle>
                                {outcome.passwords.length} new account
                                {outcome.passwords.length === 1 ? '' : 's'} were created, each with
                                a password that is <b>not stored anywhere</b> and cannot be shown
                                again. If you leave this page without saving the list, the only way
                                to give these people access is to reset each password one at a time.
                                <Box sx={{ mt: 1 }}>
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        onClick={downloadPasswords}
                                    >
                                        Download {outcome.passwords.length} password
                                        {outcome.passwords.length === 1 ? '' : 's'}
                                    </Button>
                                </Box>
                            </Alert>
                        )}

                        {outcome.failures.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <AlertTitle>{outcome.failures.length} row(s) failed while saving</AlertTitle>
                                These passed the check but could not be written. Fix them and import
                                the file again &mdash; the rows that already loaded will simply
                                update.
                                <ul>
                                    {outcome.failures.slice(0, 20).map((f: RowErr, i: number) => (
                                        <li key={i}>
                                            Row {f.row} ({f.login}): {f.problem}
                                        </li>
                                    ))}
                                </ul>
                            </Alert>
                        )}

                        {kind === 'teachers' && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Any teacher imported without a class and section has{' '}
                                <b>no access to anything</b> until one is assigned. Check the
                                Teachers list for anyone marked <i>nothing yet</i>.
                            </Alert>
                        )}

                        <Button variant="outlined" onClick={reset}>
                            Import another file
                        </Button>
                        <Button sx={{ ml: 2 }} href={`#/school-${kind.slice(0, -1)}`}>
                            View the {kind}
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default RosterImport;
